// Service for linking Sales Orders to Sales Invoices
import axiosInstance from '@/app/api/axiosInstance'
import {
  LinkedInvoiceResponse,
  LinkedSalesOrderResponse,
  LinkDocumentsRequest,
  LinkDocumentsResponse
} from '@/app/types/documentLink'

/**
 * Link a Sales Order to a Sales Invoice
 * @param salesOrderId - The ID of the sales order
 * @param invoiceId - The ID of the invoice to link
 */
export const linkSalesOrderToInvoice = async (
  salesOrderId: number,
  invoiceId: number
): Promise<LinkDocumentsResponse> => {
  const payload: LinkDocumentsRequest = {
    sales_order_id: salesOrderId,
    invoice_id: invoiceId
  }
  
  const response = await axiosInstance.post<LinkDocumentsResponse>(
    '/document-voucher-links/link-so-invoice',
    payload
  )
  
  return response.data
}

/**
 * Get the linked invoice for a Sales Order
 * @param salesOrderId - The ID of the sales order
 */
export const getInvoiceBySalesOrder = async (
  salesOrderId: number
): Promise<LinkedInvoiceResponse | null> => {
  try {
    const response = await axiosInstance.get<LinkedInvoiceResponse>(
      `/document-voucher-links/invoice-by-so/${salesOrderId}`
    )
    return response.data
  } catch (error: any) {
    // Return null if no invoice is linked (404)
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Link a Job Card to a Sales Invoice
 * @param jobCardId - The ID of the job card
 * @param invoiceId - The ID of the invoice to link
 */
export const linkJobCardToInvoice = async (
  jobCardId: number,
  invoiceId: number
): Promise<LinkDocumentsResponse> => {
  const payload = {
    job_card_id: jobCardId,
    invoice_id: invoiceId
  }

  const response = await axiosInstance.post<LinkDocumentsResponse>(
    '/document-voucher-links/link-jc-invoice',
    payload
  )

  return response.data
}

/**
 * Get the linked invoice for a Job Card
 * @param jobCardId - The ID of the job card
 */
export const getInvoiceByJobCard = async (
  jobCardId: number
): Promise<LinkedInvoiceResponse | null> => {
  try {
    const response = await axiosInstance.get<LinkedInvoiceResponse>(
      `/document-voucher-links/invoice-by-jc/${jobCardId}`
    )
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Get the source Job Card for an Invoice
 * @param invoiceId - The ID of the invoice
 */
export const getJobCardByInvoice = async (
  invoiceId: number
): Promise<{ job_card_id: number; document_number: string } | null> => {
  try {
    const response = await axiosInstance.get<{ job_card_id: number; document_number: string }>(
      `/document-voucher-links/jc-by-invoice/${invoiceId}`
    )
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Get the source Sales Order for an Invoice
 * @param invoiceId - The ID of the invoice
 */
export const getSalesOrderByInvoice = async (
  invoiceId: number
): Promise<LinkedSalesOrderResponse | null> => {
  try {
    const response = await axiosInstance.get<LinkedSalesOrderResponse>(
      `/document-voucher-links/so-by-invoice/${invoiceId}`
    )
    return response.data
  } catch (error: any) {
    // Return null if no SO is linked (404)
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}